import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { MessageCircle, Pencil, Check, RotateCcw, UserCog, Bookmark, PenLine, Zap, ListChecks, Palette, AlertTriangle } from "lucide-react";
import { isOverdue } from "./contact-inbox/utils";
import { SignatureEditor } from "./contact-inbox/SignatureEditor";
import { SnippetManager } from "./contact-inbox/SnippetManager";
import { TemplateEditor } from "./contact-inbox/TemplateEditor";
import { AssignRulesManager } from "./contact-inbox/AssignRulesManager";
import { ContactMessageCard } from "./contact-inbox/ContactMessageCard";

export const ContactInbox = ({ onCountChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [templates, setTemplates] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [snipOpen, setSnipOpen] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [team, setTeam] = useState([]);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyHandle, setReplyHandle] = useState(true);
  const [replyDeliver, setReplyDeliver] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [mine, setMine] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [selected, setSelected] = useState([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [design, setDesign] = useState(false);
  const [overdue, setOverdue] = useState(false);

  const load = () => {
    const params = [];
    if (filter !== "all") params.push(`status=${filter}`);
    if (mine) params.push("mine=true");
    if (design || overdue) params.push("category=design");
    const qs = params.length ? `?${params.join("&")}` : "";
    api.get(`/admin/contact-messages${qs}`).then((r) => {
      setData(r.data);
      if (onCountChange) onCountChange(r.data.new_count || 0);
    }).catch((e) => toast.error(errText(e)));
  };
  const loadTemplates = () => api.get("/admin/reply-templates").then((r) => setTemplates(r.data)).catch(() => {});
  const loadSnippets = () => api.get("/admin/my-snippets").then((r) => setSnippets(r.data)).catch(() => {});
  const loadTeam = () => api.get("/staff/team").then((r) => setTeam(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, mine, design, overdue]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTemplates(); loadSnippets(); if (isAdmin) loadTeam(); }, []);

  const setHandled = async (m, handled) => {
    try { await api.post(`/admin/contact-messages/${m.id}/handle`, { handled }); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/contact-messages/${id}`); toast.success("Message deleted"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const assign = async (m, assigneeId) => {
    try {
      await api.put(`/admin/contact-messages/${m.id}/assign`, { assignee_id: assigneeId === "none" ? "" : assigneeId });
      toast.success(assigneeId === "none" ? "Unassigned" : "Assigned");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const assignOptions = [{ user_id: user?.user_id, name: `${user?.name} (me)` }, ...team.filter((t) => t.user_id !== user?.user_id)];
  const passOn = async (m) => {
    try {
      const r = await api.post(`/admin/contact-messages/${m.id}/pass`);
      toast.success(`Passed to ${r.data?.assigned_to_name || "another staffer"}`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const openNote = (m) => { setNoteFor(m.id); setNoteText(""); };
  const closeNote = () => { setNoteFor(null); setNoteText(""); };
  const addNote = async (m) => {
    if (noteText.trim().length < 1) return toast.error("Write a note first");
    try {
      await api.post(`/admin/contact-messages/${m.id}/notes`, { body: noteText.trim() });
      setNoteText(""); setNoteFor(null);
      toast.success("Note added");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const deleteNote = async (m, noteId) => {
    try { await api.delete(`/admin/contact-messages/${m.id}/notes/${noteId}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSelection = () => { setSelected([]); setBulkAssignee(""); };
  const bulkAction = async (action, assigneeId) => {
    try {
      await api.post("/admin/contact-messages/bulk", { ids: selected, action, assignee_id: assigneeId || "" });
      toast.success(`${selected.length} message${selected.length > 1 ? "s" : ""} updated`);
      clearSelection();
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const teammateHint = team.map((t) => `@${t.name}`).join(", ");

  const openReply = (m) => {
    setReplyingId(m.id);
    setReplyText("");
    setAttachments([]);
    setReplyHandle(m.status !== "handled");
    setReplyDeliver(false);
  };
  const closeReply = () => { setReplyingId(null); setReplyText(""); setAttachments([]); setReplyDeliver(false); };
  const setDesignStatus = async (m, status) => {
    try {
      await api.put(`/admin/contact-messages/${m.id}/design-status`, { status });
      toast.success(status === "delivered" ? "Marked as delivered" : status === "in_design" ? "Moved to In design" : "Moved to New");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const insertTemplate = (m, tpl) => {
    const first = (m.name || "there").split(" ")[0];
    const body = (tpl.body || "").replace(/\{first\}/g, first);
    if (replyingId !== m.id) { openReply(m); setReplyHandle(!!tpl.handle); }
    setReplyText(body);
  };
  const insertSnippet = (m, snip) => {
    const first = (m.name || "there").split(" ")[0];
    const text = (snip.body || "").replace(/\{first\}/g, first);
    if (replyingId !== m.id) { openReply(m); setReplyText(text); return; }
    setReplyText((cur) => (cur ? `${cur}\n${text}` : text));
  };
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > 5) return toast.error("Up to 5 attachments per reply");
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: res } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, res.path]);
      }
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };
  const removeAttachment = (path) => setAttachments((a) => a.filter((p) => p !== path));

  const sendReply = async (m) => {
    if (replyText.trim().length < 2) return toast.error("Write a reply before sending");
    setSending(true);
    try {
      await api.post(`/admin/contact-messages/${m.id}/reply`, { body: replyText.trim(), handled: replyHandle, attachments, design_delivered: replyDeliver });
      toast.success(`Reply emailed to ${m.email}`);
      closeReply();
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setSending(false); }
  };

  const toggleHistory = async (m) => {
    if (historyFor === m.id) { setHistoryFor(null); setHistory(null); return; }
    setHistoryFor(m.id); setHistory(null);
    try { const { data: res } = await api.get(`/admin/contact-messages/${m.id}/history`); setHistory(res); }
    catch (e) { toast.error(errText(e)); setHistoryFor(null); }
  };

  const _msgs = data?.messages || [];
  const overdueCount = _msgs.filter(isOverdue).length;
  const visibleMessages = overdue
    ? _msgs.filter(isOverdue)
    : [..._msgs].sort((a, b) => (isOverdue(b) ? 1 : 0) - (isOverdue(a) ? 1 : 0));

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="contact-inbox">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><MessageCircle size={18} className="text-[#F5551E]" /> Contact messages</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setSigOpen(true)} data-testid="edit-signature-btn" className="rounded-xl h-8 gap-1 text-xs"><PenLine size={13} /> My signature</Button>
          <Button size="sm" variant="outline" onClick={() => setSnipOpen(true)} data-testid="edit-snippets-btn" className="rounded-xl h-8 gap-1 text-xs"><Bookmark size={13} /> My snippets</Button>
          {isAdmin && <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-templates-btn" className="rounded-xl h-8 gap-1 text-xs"><Pencil size={13} /> Edit templates</Button>}
          {isAdmin && <Button size="sm" variant="outline" onClick={() => setRulesOpen(true)} data-testid="assign-rules-btn" className="rounded-xl h-8 gap-1 text-xs"><Zap size={13} /> Assign rules</Button>}
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {["all", "new", "handled"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`contact-filter-${f}`} className={`text-xs font-semibold px-3 py-1.5 rounded-lg capitalize transition-colors ${filter === f ? "bg-white text-[#12234A] shadow-sm" : "text-slate-500 hover:text-[#12234A]"}`}>{f}</button>
            ))}
          </div>
          <button onClick={() => setMine((v) => !v)} data-testid="contact-filter-mine" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${mine ? "bg-[#F5551E] text-white border-[#F5551E]" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <UserCog size={13} /> Assigned to me{data?.mine_count ? <span className={`text-[10px] font-bold rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center ${mine ? "bg-white text-[#F5551E]" : "bg-[#F5551E] text-white"}`}>{data.mine_count}</span> : null}
          </button>
          <button onClick={() => setDesign((v) => !v)} data-testid="contact-filter-design" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${design ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <Palette size={13} /> Design requests{data?.design_count ? <span className={`text-[10px] font-bold rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center ${design ? "bg-white text-[#12234A]" : "bg-[#12234A] text-white"}`}>{data.design_count}</span> : null}
          </button>
          <button onClick={() => setOverdue((v) => !v)} data-testid="contact-filter-overdue" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${overdue ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <AlertTriangle size={13} /> Overdue 48h+{overdueCount ? <span className={`text-[10px] font-bold rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center ${overdue ? "bg-white text-red-600" : "bg-red-600 text-white"}`}>{overdueCount}</span> : null}
          </button>
        </div>
      </div>
      {selected.length > 0 && (
        <div className="flex items-center gap-2 mb-3 p-2.5 rounded-xl bg-[#12234A] text-white flex-wrap" data-testid="contact-bulk-bar">
          <span className="text-xs font-semibold px-1"><ListChecks size={14} className="inline mr-1" />{selected.length} selected</span>
          <Button size="sm" onClick={() => bulkAction("handle")} data-testid="bulk-handle-btn" className="rounded-lg h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1"><Check size={13} /> Mark handled</Button>
          <Button size="sm" onClick={() => bulkAction("reopen")} data-testid="bulk-reopen-btn" variant="outline" className="rounded-lg h-7 text-xs bg-transparent border-white/30 text-white hover:bg-white/10 gap-1"><RotateCcw size={13} /> Reopen</Button>
          {isAdmin && (
            <Select value={bulkAssignee} onValueChange={(v) => { setBulkAssignee(v); bulkAction("assign", v === "none" ? "" : v); }}>
              <SelectTrigger data-testid="bulk-assign-trigger" className="h-7 rounded-lg text-xs w-[160px] bg-transparent border-white/30 text-white gap-1"><UserCog size={12} /><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassign</SelectItem>
                {assignOptions.filter((o) => o.user_id).map((o) => (
                  <SelectItem key={o.user_id} value={o.user_id} data-testid={`bulk-assign-${o.user_id}`}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <button onClick={clearSelection} data-testid="bulk-clear-btn" className="text-xs text-white/70 hover:text-white ml-auto px-2">Clear</button>
        </div>
      )}
      {!data ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : visibleMessages.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center">{overdue ? "No design requests waiting over 48h. 🎉" : `No ${mine ? "messages assigned to you" : `${filter === "all" ? "" : filter} messages`}.`}</p>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {visibleMessages.map((m) => (
            <ContactMessageCard
              key={m.id}
              m={m} isAdmin={isAdmin} user={user} assignOptions={assignOptions}
              templates={templates} snippets={snippets} teammateHint={teammateHint}
              isSelected={selected.includes(m.id)} onToggleSelect={toggleSelect}
              replyingId={replyingId} replyText={replyText} setReplyText={setReplyText}
              attachments={attachments} uploading={uploading} sending={sending}
              replyHandle={replyHandle} setReplyHandle={setReplyHandle}
              replyDeliver={replyDeliver} setReplyDeliver={setReplyDeliver}
              historyFor={historyFor} history={history}
              noteFor={noteFor} noteText={noteText} setNoteText={setNoteText}
              setHandled={setHandled} remove={remove} assign={assign} passOn={passOn}
              openNote={openNote} addNote={addNote} deleteNote={deleteNote} closeNote={closeNote}
              openReply={openReply} closeReply={closeReply} setDesignStatus={setDesignStatus}
              insertTemplate={insertTemplate} insertSnippet={insertSnippet}
              onUpload={onUpload} removeAttachment={removeAttachment}
              sendReply={sendReply} toggleHistory={toggleHistory}
            />
          ))}
        </div>
      )}
      {isAdmin && <TemplateEditor open={editOpen} onOpenChange={setEditOpen} templates={templates} onSaved={loadTemplates} />}
      <SignatureEditor open={sigOpen} onOpenChange={setSigOpen} />
      <SnippetManager open={snipOpen} onOpenChange={setSnipOpen} snippets={snippets} onChanged={loadSnippets} />
      {isAdmin && <AssignRulesManager open={rulesOpen} onOpenChange={setRulesOpen} team={team} />}
    </div>
  );
};
